/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import {
    Payload,
    Diagnostics,
    QoE,
    Operations,
    SageTransport,
    ServiceApiLogs,
    AnalyticsConstants,
} from '@tivocorp/tivo-webapp-loggers';
import {
    AppStartOperationsLogger,
    Log,
    NFRLoggings,
    OperationsLogger,
    QoEAppLogger,
    SageLogger,
    VuLogger,
} from '../../core/logging';
import { NetworkInterceptor } from '../../core/device/NetworkInterceptor';
import { KAVERI_GLOBAL_CATCH_NAME } from '../../config';
import { AppUtils } from '../../core/AppUtils';
import { DeviceModel } from '../../core/device/DeviceModel';
import { ServicesClientImpl } from '../../core/impls/ServicesClientImpl';
import { DeviceFeatures } from '../../core/device/DeviceFeatures';
import { showDefaultScreen } from '../screens/screens.reducers';
import { NFREvents } from '../../core/enums/NFREvents';
import { Storage } from '../../core/storage/Storage';
import { RecoveryMechanism } from '../../core/recoverymechanism/RecoveryMechanism';
import { GlobalCatch } from '../../core/error/GlobalCatch';
import { setSystemHealthStatsInterval } from '../healthstats/systemhealthstats.reducers';
import {
    hideErrorOverlay,
    showTemporaryServiceConnectionIssueOverlay,
} from '../error/erroroverlays.reducers';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import {
    BodyInfoError,
    BrandingUiBundleError,
    DeviceFeatureSearchError,
    EmptyResponseError,
    ErrorTypes,
    MsoServiceIdNotFoundError,
    PartialResponseError,
    SlsServiceEndpointsError,
} from '../../core/error';
import { VideoManager } from '../../core/videoplayer';
import { NetworkSentinel } from '../../core/network';

const TAG = 'postsignin';

const initializeDiagnosticsLogging = () => {
    const globalParam: any = DeviceModel.getGlobalParams();
    globalParam.shellAppVersion = DeviceModel.getTiVoSettings().getShellVersion();
    AppUtils.filterUnknownOrEmpty(globalParam);
    const globalPayload: Payload = new Payload(globalParam);
    const serviceApiGlobalPayload: Payload = new Payload(DeviceModel.getGlobalParams());
    const isDiagnosticLoggingEnabled = DeviceFeatures.isDiagnosticLoggingEnabled();

    if (isDiagnosticLoggingEnabled) {
        Diagnostics.setGlobals(globalPayload);
        Diagnostics.setLogLevel(DeviceFeatures.getBackhaulVerbosity());
        Diagnostics.getInstance().setTransport(SageLogger.getInstance().getDiagnosticsTransport());
        ServiceApiLogs.setGlobals(serviceApiGlobalPayload);
        NetworkInterceptor.upload(SageLogger.getInstance().getDiagnosticsTransport());
    } else {
        Diagnostics.disabled();
    }

    Diagnostics.noConsole();
};

const updateOperationsLogging = () => {
    const globalParam = DeviceModel.getGlobalParams();

    AppUtils.filterUnknownOrEmpty(globalParam);

    const globalPayload: Payload = new Payload(globalParam);
    Operations.setGlobals(globalPayload);
    Operations.noConsole();
    Operations.getInstance().setTransport(SageLogger.getInstance().getOperationsTransport());
};

const initializeQoeLogging = () => {
    const globalParam: any = DeviceModel.getGlobalParams();

    AppUtils.filterUnknownOrEmpty(globalParam);

    const globalPayload: Payload = new Payload(globalParam);
    QoE.setGlobals(globalPayload);
    QoE.noConsole();
    QoE.getInstance().setTransport(SageLogger.getInstance().getOperationsTransport());
};

const initializeSageLogger = () => {
    Diagnostics.info(TAG, 'Inside initializeSageLogger');
    SageLogger.getInstance().configure({
        partnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
    });
    initializeDiagnosticsLogging();
    initializeQoeLogging();
    updateOperationsLogging();

    Diagnostics.info(TAG, 'Application Start');
    QoEAppLogger.logApplicationStart();
    Operations.logDeviceInfo(
        new Payload({
            lang: DeviceModel.getTVProperties().getLang(),
            locale: DeviceModel.getTVProperties().getLocale(),
            macAddress: DeviceModel.getTVProperties().getMacAddress(),
            region: DeviceModel.getTVProperties().getRegion(),
            timezone: DeviceModel.getTVProperties().getTimezone(),
        }),
    );
    uploadApplicationStartupLogs();

    Storage.getInstance().removeItem(KAVERI_GLOBAL_CATCH_NAME);
};

const updateVuLogginGlobalParams = () => {
    VuLogger.updateGlobalParams({
        msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
    });
};

const uploadApplicationStartupLogs = () => {
    const globalPayload: Payload = new Payload(DeviceModel.getGlobalParams());

    // Upload global caches.
    const globalCatch = new GlobalCatch(KAVERI_GLOBAL_CATCH_NAME);
    globalCatch
        .upload(SageLogger.getInstance().getDiagnosticsTransport() as SageTransport, globalPayload)
        .then((status) => {
            if (status) globalCatch.clear();
        });

    Diagnostics.setGlobals(globalPayload);
    Diagnostics.info(TAG, 'Application startup completed.');
};

/**
 * The post signin activities
 */
export const launchHomeScreen = () => async (dispatch: Function) => {
    Diagnostics.info(TAG, 'Inside launchHomeScreen');
    const services = new ServicesClientImpl();
    NetworkSentinel.getInstance().addService(services.trackNetworkChange.bind(services));

    // Step 1: service initialization.
    try {
        Diagnostics.info(TAG, 'Step 1: service initialization.');
        AppStartOperationsLogger.logSlsServiceEndpointsGet();
        await services.loadSlsServiceEndpoints();
        AppStartOperationsLogger.logSlsServiceEndpointsSuccessful();
    } catch (error: any) {
        Diagnostics.error(TAG, 'Failed to get service init params.', error);
        Log.e(TAG, 'Failed to get service init params.', error);
        let slsError = error;
        if (!(error instanceof PartialResponseError || error instanceof EmptyResponseError)) {
            const serviceInitError = error?.response?.data;
            slsError = new SlsServiceEndpointsError(
                serviceInitError?.message,
                serviceInitError?.code,
                Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
            );
        }
        AppStartOperationsLogger.logSlsServiceEndpointsFailed(slsError);
        AppStartOperationsLogger.logUIErrorCode(slsError.getCode());
        dispatch(showTemporaryServiceConnectionIssueOverlay(slsError));

        Diagnostics.info(TAG, 'Wait for service to recover.');
        await services.waitForSlsServiceEndpoints();
        AppStartOperationsLogger.logSlsServiceEndpointsRestoration();
        dispatch(hideErrorOverlay());
    }

    // Step 2: load device features
    try {
        Diagnostics.info(TAG, 'Step 2: load device features');
        AppStartOperationsLogger.logDeviceFeatureGet();
        await services.fetchDeviceFeaturesFromService();
        AppStartOperationsLogger.logDeviceFeatureSuccessful();
    } catch (error: any) {
        const deviceFeaturesInitError = error?.response?.data;
        const deviceFeaturesError = new DeviceFeatureSearchError(
            deviceFeaturesInitError?.message,
            deviceFeaturesInitError?.code,
            Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
        );
        dispatch(showTemporaryServiceConnectionIssueOverlay(deviceFeaturesError));
        Diagnostics.error(TAG, 'Failed to load device features.', error);
        Log.e(TAG, 'Failed to load device features.', error);
        AppStartOperationsLogger.logDeviceFeatureFailed(deviceFeaturesError);
        AppStartOperationsLogger.logUIErrorCode(deviceFeaturesError.getCode());
        await services.waitForDeviceFeaturesFromService();
        AppStartOperationsLogger.logDeviceFeatureRestoration();
    } finally {
        DeviceFeatures.initMsoServiceIdFeature(
            services.getDeviceFeatureByName(
                DeviceFeatures.MSO_SERVICE_ID,
                DeviceFeatures.getDefaultMsoServiceId(),
            ),
        );

        if (!DeviceFeatures.getMsoServiceId()) {
            const msoServiceIdError = new MsoServiceIdNotFoundError(
                ErrorTypes.MSO_SERVICE_ID_NOT_FOUND_C1001,
                'MSO_SERVICE_ID_NOT_FOUND',
                Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
            );
            AppStartOperationsLogger.logMsoServiceIdNotFound(msoServiceIdError);
            AppStartOperationsLogger.logUIErrorCode(msoServiceIdError.getCode());
        }
        if (DeviceFeatures.isMsoServiceIdChanged()) {
            Diagnostics.info(TAG, 'The msoServiceId changed. Clearing app data');
            Storage.getInstance().clearData();
        }

        DeviceFeatures.initBackhaulFeature(
            services.getDeviceFeatureByName(
                DeviceFeatures.FORCE_BACKHAUL,
                DeviceFeatures.getDefaultForceBackhaul(),
            ),
        );

        DeviceFeatures.initSocuFeature(
            services.getDeviceFeatureByName(DeviceFeatures.APP_ACT_CLIENT_SOCU, {}),
        );

        NetworkInterceptor.logging(DeviceFeatures.isServiceApiLoggingEnabled());
    }

    // Step 3: get body info.
    try {
        Diagnostics.info(TAG, 'Step 3: get body info.');
        AppStartOperationsLogger.logBodyInfoGet();
        await services.loadBodyInfo();
        AppStartOperationsLogger.logBodyInfoSuccessful();
    } catch (error: any) {
        const bodyInfoServiceError = error?.response?.data;
        const bodyInfoError = new BodyInfoError(
            bodyInfoServiceError?.message,
            bodyInfoServiceError?.code,
            Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
        );
        dispatch(showTemporaryServiceConnectionIssueOverlay(bodyInfoError));
        Diagnostics.error(TAG, 'Failed to load body info', error);
        AppStartOperationsLogger.logBodyInfoFailed(bodyInfoError);
        AppStartOperationsLogger.logUIErrorCode(bodyInfoError.getCode());
        Diagnostics.info(TAG, 'Wait for body info to load');
        await services.waitForBodyInfo();
        AppStartOperationsLogger.logBodyInfoRestoration();
        dispatch(hideErrorOverlay());
    } finally {
        Log.i(TAG, 'body info loaded.');
        initializeSageLogger();
        updateVuLogginGlobalParams();
        dispatch(setSystemHealthStatsInterval());
        VuLogger.logLoginEvent(DeviceFeatures.getMsoServiceId());
        VuLogger.logIdentifyEvent(DeviceModel.getTiVoSettings().getPartnerCustomerId());
    }

    // Step 4: get branding UI bundle.
    try {
        NFRLoggings.log(NFREvents.START_LOADING_GUIDE);
        await services.loadBrandingUiBundleInfo();
    } catch (error: any) {
        const brandingError = error?.response?.data;
        const brandingUiError = new BrandingUiBundleError(
            brandingError?.message,
            brandingError?.code,
        );
        Diagnostics.error(TAG, 'Failed at getBrandingUIBundleInfo', error);
        Diagnostics.info(TAG, 'Wait for brandingUiBundle');
        const payload = new Payload();
        payload.add(AnalyticsConstants.Fields.EVENT_NAME, 'BRANDING_FAILED');
        payload.add(AnalyticsConstants.Fields.ERROR_CODE, brandingUiError.getCode());
        payload.add(AnalyticsConstants.Fields.EVENT_TYPE, brandingUiError.getType());
        payload.add(AnalyticsConstants.Fields.MESSAGE, brandingUiError.getMessage());
        Operations.log(payload);
        OperationsLogger.logUIErrorCode(brandingUiError.getCode());
        await services.waitForBrandingUiBundleInfo();
    } finally {
        Log.i(TAG, 'branding ui bundle loaded.');
    }

    // Step 5: load channels info.
    try {
        NFRLoggings.log(NFREvents.START_LOADING_CHANNELS);
        await services.loadChannelsInfo();
        NFRLoggings.log(NFREvents.FINISH_LOADING_CHANNELS);
    } catch (error: any) {
        Log.e(TAG, 'Failed to load channels info.', error);
    } finally {
        Diagnostics.info(TAG, 'Step 5.1: Show default screen');
        dispatch(showDefaultScreen());
    }

    // Step 6: load internal ratings.
    services.loadInternalRatings().catch((error) => {
        Log.e(TAG, 'Failed to load internal rating.', error);
        services.recoverInternalRatingsInBackground();
    });

    services.loadVpConfigurations().catch((error) => {
        Log.e(TAG, 'Failed to load video provider configurations.', error);
    });

    // Step 7: load in home location info.
    services
        .loadInHomeLocationInfo()
        .catch((error) => {
            Log.e(TAG, 'Failed to load in home location info.', error);
            Diagnostics.error(TAG, 'Failed to load in home location info.', error);
            RecoveryMechanism.callServiceWithRecovery(
                services.loadInHomeLocationInfo.bind(services),
                [],
            );
        })
        .finally(() => {
            VideoManager.getInstance().ready();
            Diagnostics.info(TAG, 'Step 7: Video manager ready');
        });
};
